import { Component, OnDestroy, OnInit } from '@angular/core';

import { WorkOrder } from './work-order.model';
import { WorkOrderService } from '@app/dashboard/work-order.service';
import { FilterOption } from './filter-option';
import { createFilters } from './filter-list-factory';
import { UserService } from '@app/core/services/user.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { NetworkStatusService } from '@app/core/services/network-status.service';
import { OfflineDataDb, WorkOrderItem } from '@app/core/db/offline-data';
import { filter, take, takeUntil, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { SortOption } from '@app/dashboard/sort-option';
import { BadgeNotification } from '@app/core/model/badge-notification.model';
import { DropdownDialogComponent, DropdownDialogOption } from '@app/shared/dropdown-dialog/dropdown-dialog.component';
import { DashboardState } from '@app/core/store/reducers/dashboard';
import { getSearchText } from '@app/core/store/selectors/dashboard.selector';
import { Observable, Subject, interval, merge, of } from 'rxjs';
import { env } from 'src/environments/env';
import { Store } from '@ngrx/store';
import { ClearSearchText, SetSearchText } from '@dwo/actions';
import { ChangeLogService } from '@app/core/services/change-log.service';
import { WorkOrderDetailsService } from '@app/work-order/work-order-details.service';
import { VersionCheckService } from '@app/core/services/version-check.service';
import { ChangeSyncService } from '@app/core/services/change-sync.service';
import { LoaderService } from '@app/header/loader/loader.service';
import { ApiLoadErrorService } from '@app/core/services/api-load-error.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  constructor(
    private workOrderService: WorkOrderService,
    private userService: UserService,
    private webSocketService: WebSocketService,
    private networkStatusService: NetworkStatusService,
    private offlineData: OfflineDataDb,
    private dialog: MatDialog,
    private store: Store<DashboardState>,
    private changeLogService: ChangeLogService,
    private workOrderDetailsService: WorkOrderDetailsService,
    private versionCheckService: VersionCheckService,
    private changeSyncService: ChangeSyncService,
    private loaderService: LoaderService,
    private apiLoadErrorService: ApiLoadErrorService,
  ) {
    this.workOrders = localStorage.getItem('workOrders') ? JSON.parse(localStorage.getItem('workOrders')) : [];
  }

  private workOrders: WorkOrder[] = [];
  public sites: string[];
  public taskNos: string[];
  public selectedSites: string[];
  public selectedTaskNos: string[];

  private filters: FilterOption<WorkOrder>[] = [];
  private selectedFilter: FilterOption<WorkOrder>;

  public searchText$: Observable<string>;

  public showSearchBar = false;
  public loading = true;
  public isOnline = navigator.onLine;
  public isBackendReachable = true;
  public isProjectAuthorized = true;
  public showOfflineFallbackBanner = false;
  public isViewingDownloadedContent = false;
  public loadErrorMessage: string | null = null;
  public onlineSyncBannerState: 'hidden' | 'syncing' | 'saved' = 'hidden';
  public recoveryHadPendingChanges = false;
  public isRetryingOnline = false;
  private readonly offlineRecoveryRetryIntervalMs = env.get().offlineRecoveryRetryIntervalMs ?? 10_000;
  public pendingChangeCount = 0;
  public pendingWorkOrderCount = 0;
  public hasMultipleActiveProjects = false;
  public isSyncingAllDownloaded = false;
  public isClearingOfflineData = false;
  private awaitingOnlineRecovery = false;
  private hasRecoveredOnlineData = false;
  private projectNameById = new Map<string, string>();
  private pendingChangesByWorkOrder = new Map<string, number>();
  private workOrdersWithSavedChanges = new Set<string>();

  public workOrdersWithNotification: BadgeNotification[] = [];
  private destroy$ = new Subject<void>();

  private static toDropdownDialogOption(sortOption: SortOption) {
    return { label: sortOption.label, value: `${sortOption.field}${sortOption.direction}` };
  }

  async ngOnInit() {
    this.loadErrorMessage = null;
    this.apiLoadErrorService.clear();
    this.apiLoadErrorService.changes.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.loadErrorMessage = state?.message ?? null;
    });

    try {
      await this.workOrderService.load();
      this.isProjectAuthorized = true;
      this.updateOfflineFallbackBanner();
    } catch (error) {
      if (error.message === 'User unauthorized for this project') {
        this.isProjectAuthorized = false;
      }

      // Keep offline monitoring active even when initial load fails,
      // so we can recover automatically when connectivity returns.
      this.networkStatusService
        .isOnline()
        .pipe(takeUntil(this.destroy$))
        .subscribe((online) => {
          this.isOnline = online;
          if (!online) {
            this.onlineSyncBannerState = 'hidden';
            this.awaitingOnlineRecovery = false;
            this.hasRecoveredOnlineData = false;
          }
          this.updateOfflineFallbackBanner();
        });

      this.loaderService.backendReachableStream.pipe(takeUntil(this.destroy$)).subscribe((reachable) => {
        this.isBackendReachable = reachable;
        this.updateOfflineFallbackBanner();
      });

      merge(of(null), this.changeLogService.changes$)
        .pipe(
          takeUntil(this.destroy$),
          switchMap(() => this.changeLogService.getAllPending()),
        )
        .subscribe((events) => {
          this.applyPendingChangeState(events);

          if (
            this.pendingChangeCount === 0 &&
            this.onlineSyncBannerState === 'syncing' &&
            this.isOnline &&
            !this.showOfflineFallbackBanner
          ) {
            this.onlineSyncBannerState = 'saved';
          }
        });

      this.changeSyncService.syncEvents$.pipe(takeUntil(this.destroy$)).subscribe(async (event) => {
        if (event.state === 'syncing') {
          if (this.hasRecoveredOnlineData || this.awaitingOnlineRecovery) {
            this.onlineSyncBannerState = 'syncing';
          }
          return;
        }

        if (event.state === 'success' && this.isOnline) {
          const pendingEvents = await this.changeLogService.getAllPending();
          this.applyPendingChangeState(pendingEvents);

          if (this.hasRecoveredOnlineData && this.pendingChangeCount === 0 && !this.showOfflineFallbackBanner) {
            this.onlineSyncBannerState = 'saved';
          } else if (this.hasRecoveredOnlineData && !this.showOfflineFallbackBanner) {
            this.onlineSyncBannerState = 'syncing';
          }
          return;
        }

        if (event.state === 'error' && !this.showOfflineFallbackBanner) {
          this.onlineSyncBannerState = 'hidden';
        }
      });

      interval(this.offlineRecoveryRetryIntervalMs)
        .pipe(
          takeUntil(this.destroy$),
          filter(() => this.showOfflineFallbackBanner && !this.isRetryingOnline),
        )
        .subscribe(() => this.retryLoadWorkOrders());

      this.updateOfflineFallbackBanner();
      this.loading = false;
      return;
    }
    try {
      await this.userService.load();
    } catch (error) {
      console.warn('[DASHBOARD] Failed to load user profile. Continuing with offline/cached work orders.', error);
    }

    this.initializeProjectContext();
    const savedSites = localStorage.getItem('selectedSites');
    const savedTaskNos = localStorage.getItem('selectedTaskNos');
    this.workOrders = this.workOrderService.get();
    this.sites = this.getAllSitesForWorkOrders();
    this.taskNos = this.getAllTaskNosForWorkOrders();
    this.selectedSites = (savedSites ? JSON.parse(savedSites) : []).filter((site) => this.sites.includes(site));
    this.selectedTaskNos = (savedTaskNos ? JSON.parse(savedTaskNos) : []).filter((taskNo) =>
      this.taskNos.includes(taskNo),
    );

    this.searchText$ = this.store.select(getSearchText);

    this.createFilters();
    this.changeSelectedSiteFilters();
    this.changeSelectedTaskFilters();
    this.workOrdersWithNotification = [];

    this.searchText$.subscribe((searchText) => {
      this.workOrderService.searchText = searchText;
      this.workOrders = this.workOrderService.get();
    });

    this.networkStatusService
      .isOnline()
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        this.isOnline = online;
        if (!online) {
          this.onlineSyncBannerState = 'hidden';
          this.awaitingOnlineRecovery = false;
          this.hasRecoveredOnlineData = false;
        }
        this.updateOfflineFallbackBanner();
      });

    this.loaderService.backendReachableStream.pipe(takeUntil(this.destroy$)).subscribe((reachable) => {
      this.isBackendReachable = reachable;
      this.updateOfflineFallbackBanner();
    });

    // Reactively update pending change counts across all work orders.
    merge(of(null), this.changeLogService.changes$)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.changeLogService.getAllPending()),
      )
      .subscribe((events) => {
        this.applyPendingChangeState(events);

        if (
          this.pendingChangeCount === 0 &&
          this.onlineSyncBannerState === 'syncing' &&
          this.isOnline &&
          !this.showOfflineFallbackBanner
        ) {
          this.onlineSyncBannerState = 'saved';
        }
      });

    this.changeSyncService.syncEvents$.pipe(takeUntil(this.destroy$)).subscribe(async (event) => {
      if (event.state === 'syncing') {
        if (this.hasRecoveredOnlineData || this.awaitingOnlineRecovery) {
          this.onlineSyncBannerState = 'syncing';
        }
        return;
      }

      if (event.state === 'success' && this.isOnline) {
        const pendingEvents = await this.changeLogService.getAllPending();
        this.applyPendingChangeState(pendingEvents);

        if (this.hasRecoveredOnlineData && this.pendingChangeCount === 0 && !this.showOfflineFallbackBanner) {
          this.onlineSyncBannerState = 'saved';
        } else if (this.hasRecoveredOnlineData && !this.showOfflineFallbackBanner) {
          this.onlineSyncBannerState = 'syncing';
        }
        return;
      }

      if (event.state === 'error' && !this.showOfflineFallbackBanner) {
        this.onlineSyncBannerState = 'hidden';
      }
    });

    // Auto-retry connectivity using the configured interval while the offline banner is showing.
    interval(this.offlineRecoveryRetryIntervalMs)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.showOfflineFallbackBanner && !this.isRetryingOnline),
      )
      .subscribe(() => this.retryLoadWorkOrders());

    this.webSocketService.badgeNotifications$.subscribe((notifications) => {
      if (notifications) {
        let reloadWorkOrders = false;
        this.workOrdersWithNotification = notifications
          .filter((notification: BadgeNotification) => notification.name.endsWith('WorkOrderEntity'))
          .map((notification) => {
            if (notification.type === 'NEW') {
              reloadWorkOrders = true;
            }
            return notification;
          });

        if (reloadWorkOrders) {
          this.refreshWorkOrdersFromService().then(() => {
            reloadWorkOrders = false;
          });
        }
      }
    });

    this.loading = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.workOrderService.unload();
  }

  onKeyUp(event) {
    this.store.dispatch(new SetSearchText(event));
  }

  getAllWorkOrders(): WorkOrder[] {
    return this.workOrders;
  }

  public areFiltersApplied(): boolean {
    return (
      (this.selectedSites && this.selectedSites.length > 0) || (this.selectedTaskNos && this.selectedTaskNos.length > 0)
    );
  }

  resetFilters() {
    this.selectedSites = [];
    this.selectedTaskNos = [];
    this.changeSelectedSiteFilters();
    this.changeSelectedTaskFilters();
  }

  changeSelectedSiteFilters() {
    const availableSites = this.getAllSitesForWorkOrders();
    this.selectedSites = (this.selectedSites || []).filter((site) => availableSites.includes(site));

    const siteWorkOrders = this.workOrderService
      .getAll()
      .filter((wo) => !this.selectedSites.length || this.selectedSites.includes(wo.siteNo));
    const availableTaskNos = Array.from(
      new Set(siteWorkOrders.map((wo) => wo.taskNo).filter((taskNo) => !!taskNo)),
    ).sort((a, b) => a.localeCompare(b));

    this.selectedTaskNos = this.selectedTaskNos.filter((taskNo) => availableTaskNos.includes(taskNo));

    this.taskNos = availableTaskNos;
    this.workOrderService.setSelectedSites(this.selectedSites);
    localStorage.setItem('selectedSites', JSON.stringify(this.selectedSites));
    this.workOrders = this.workOrderService.get();
    this.changeSelectedTaskFilters();
  }

  changeSelectedTaskFilters() {
    this.workOrderService.setSelectedTaskNos(this.selectedTaskNos);
    localStorage.setItem('selectedTaskNos', JSON.stringify(this.selectedTaskNos));
    this.workOrders = this.workOrderService.get();
  }

  getAllSitesForWorkOrders(): string[] {
    const siteNos = this.workOrders.map((workOrder) => workOrder.siteNo).filter((siteNo) => !!siteNo);
    // Return unique siteNos
    return Array.from(new Set(siteNos)).sort((a, b) => a.localeCompare(b));
  }

  getAllTaskNosForWorkOrders(): string[] {
    const taskNos = this.workOrders.map((workOrder) => workOrder.taskNo).filter((taskNo) => !!taskNo);
    // Return unique taskNos
    return Array.from(new Set(taskNos)).sort((a, b) => a.localeCompare(b));
  }

  hasWorkOrders(): boolean {
    return this.getWorkOrders().length > 0;
  }

  public getWorkOrderService(): WorkOrderService {
    return this.workOrderService;
  }

  getWorkOrders(): WorkOrder[] {
    const workOrders = [...this.workOrders];
    if (this.selectedFilter) {
      return workOrders.filter(this.selectedFilter.filter);
    }
    return workOrders;
  }

  getProjectTitle(projectId?: string): string {
    if (!projectId) {
      return '';
    }

    return this.projectNameById.get(projectId) ?? '';
  }

  hasNotification(workOrderId: string): boolean {
    return (
      this.workOrdersWithNotification.filter(
        (notification) => notification.id === workOrderId && notification.children.length > 0,
      ).length > 0
    );
  }

  isNewNotification(workOrderId: string): boolean {
    return (
      this.workOrdersWithNotification.filter(
        (notification) => notification.id === workOrderId && notification.type === 'NEW',
      ).length > 0
    );
  }

  refreshPerformed(workOrderId: string): boolean {
    return (
      this.workOrdersWithNotification.filter(
        (notification) => notification.id === workOrderId && notification.refreshTriggered,
      ).length > 0
    );
  }

  getFilters(): FilterOption<WorkOrder>[] {
    return this.filters;
  }

  openSearchBar() {
    this.showSearchBar = true;
  }

  acceptSearch() {
    this.showSearchBar = false;
  }

  closeSearchBar() {
    this.showSearchBar = false;
    this.store.dispatch(new ClearSearchText());
  }

  hasDisclaimer(): boolean {
    if (this.selectedFilter?.id === 'filter-downloaded') {
      for (const wo of this.workOrders) {
        if (this.hasNotification(wo.id) && !this.refreshPerformed(wo.id)) {
          return true;
        }
      }
    }

    return false;
  }

  public getSelectedSortingOption(): SortOption {
    return this.workOrderService.getSelectedSortingOption();
  }

  public getSelectedFilter(): FilterOption<WorkOrder> {
    return this.selectedFilter;
  }

  public setSelectedFilter(value: FilterOption<WorkOrder>): void {
    this.selectedFilter = value;
  }

  public updateFilters(): void {
    this.createFilters();
  }

  public isDownloadedTabSelected(): boolean {
    return this.selectedFilter?.id === 'filter-downloaded';
  }

  public async syncAllDownloaded(): Promise<void> {
    if (this.isSyncingAllDownloaded) {
      return;
    }

    this.isSyncingAllDownloaded = true;
    try {
      const downloadedWorkOrders = this.workOrders.filter((wo) => wo.downloaded);

      for (const workOrder of downloadedWorkOrders) {
        workOrder.isDownloading = true;
        try {
          const selection = await this.workOrderDetailsService.getStoredDocumentSelection(workOrder.id);
          await this.workOrderDetailsService.downloadWorkOrder(workOrder.id, { selection: selection ?? undefined });
        } catch (error) {
          console.warn(`[DASHBOARD] Failed to sync downloaded work order ${workOrder.id}`, error);
        } finally {
          workOrder.isDownloading = false;
        }
      }

      await this.refreshOfflineMetadata();
      this.updateFilters();
      // Ensure we stay on the Downloaded tab after syncing
      const downloadedFilter = this.filters.find((f) => f.id === 'filter-downloaded');
      if (downloadedFilter) {
        this.setSelectedFilter(downloadedFilter);
      }
    } finally {
      this.isSyncingAllDownloaded = false;
    }
  }

  public async clearOfflineData(): Promise<void> {
    if (this.isClearingOfflineData || this.showOfflineFallbackBanner) {
      return;
    }

    this.isClearingOfflineData = true;
    try {
      await this.offlineData.workOrderFiles.clear();
      await this.offlineData.workOrderDataItems.clear();
      await this.offlineData.workOrders.clear();

      this.workOrders.forEach((wo) => {
        wo.downloaded = false;
        wo.lastSyncedAt = null;
      });

      this.updateFilters();
    } finally {
      this.isClearingOfflineData = false;
    }
  }

  public async onWorkOrderDownloadChange(workOrder: WorkOrder, isDownloaded: boolean): Promise<void> {
    workOrder.downloaded = isDownloaded;
    await this.refreshOfflineMetadata();
    this.updateFilters();
    // Ensure we stay on the Downloaded tab if it was previously selected
    if (this.selectedFilter?.id === 'filter-downloaded') {
      const downloadedFilter = this.filters.find((f) => f.id === 'filter-downloaded');
      if (downloadedFilter) {
        this.setSelectedFilter(downloadedFilter);
      }
    }
  }

  private createFilters() {
    const user = this.userService.get();
    const prevSelectedId = this.selectedFilter?.id;
    this.filters = createFilters(this.workOrders, user);
    if (prevSelectedId) {
      const restored = this.filters.find((f) => f.id === prevSelectedId);
      this.selectedFilter = restored || this.filters[0];
    } else {
      this.selectedFilter = this.filters[0];
    }
  }

  private initializeProjectContext(): void {
    const userProjects = this.userService.get()?.projects ?? [];
    this.hasMultipleActiveProjects = userProjects.length > 1;
    this.projectNameById = new Map(
      userProjects.filter((project) => !!project?.id).map((project) => [project.id, project.name ?? '']),
    );
  }

  public openSortingOptionDialog() {
    const sortingOptions = this.workOrderService.getSortingOptions();

    const dialog = this.dialog.open(DropdownDialogComponent, {
      maxWidth: undefined,
      width: undefined,
      data: {
        selected: DashboardComponent.toDropdownDialogOption(this.workOrderService.getSelectedSortingOption()),
        options: sortingOptions.map(DashboardComponent.toDropdownDialogOption),
      },
    });

    dialog
      .afterClosed()
      .pipe(
        filter((option) => !!option),
        take(1),
      )
      .subscribe((result: DropdownDialogOption) => {
        this.workOrderService.setSelectedSortingOption(
          sortingOptions[sortingOptions.map((e) => `${e.field}${e.direction}`).indexOf(result.value)],
        );
        this.workOrders = this.workOrderService.get();
      });
  }

  async reorder() {
    this.workOrders = this.workOrderService.get();
  }

  public async retryLoadWorkOrders() {
    if (this.isRetryingOnline) {
      return;
    }

    this.isRetryingOnline = true;
    try {
      this.apiLoadErrorService.clear();
      const versionProbeSucceeded = await this.versionCheckService.probeVersion();
      if (!versionProbeSucceeded) {
        return;
      }

      this.awaitingOnlineRecovery = this.isOnline && this.showOfflineFallbackBanner;
      this.recoveryHadPendingChanges = this.pendingChangeCount > 0;

      if (this.pendingChangeCount > 0) {
        this.onlineSyncBannerState = 'syncing';
      }

      await this.refreshWorkOrdersFromService();
    } finally {
      this.isRetryingOnline = false;
    }
  }

  private async refreshWorkOrdersFromService() {
    this.apiLoadErrorService.clear();
    await this.workOrderService.load();

    this.workOrders = this.workOrderService.get();
    this.applyPerCardPendingChangeLabels();
    this.updateOfflineFallbackBanner();

    if (this.awaitingOnlineRecovery && this.isOnline && !this.showOfflineFallbackBanner) {
      this.awaitingOnlineRecovery = false;
      this.hasRecoveredOnlineData = true;
      if (this.pendingChangeCount === 0) {
        this.onlineSyncBannerState = 'saved';
      } else {
        this.onlineSyncBannerState = 'syncing';
      }
    }

    this.sites = this.getAllSitesForWorkOrders();
    this.taskNos = this.getAllTaskNosForWorkOrders();
    this.selectedSites = (this.selectedSites || []).filter((site) => this.sites.includes(site));
    this.selectedTaskNos = (this.selectedTaskNos || []).filter((taskNo) => this.taskNos.includes(taskNo));

    this.changeSelectedSiteFilters();
    this.updateFilters();
  }

  private async refreshOfflineMetadata(): Promise<void> {
    const downloadedWorkOrders = await this.offlineData.workOrders.toArray();
    const offlineWorkOrderMap = new Map(downloadedWorkOrders.map((item) => [item.workOrderId, item]));

    this.workOrders.forEach((wo) => {
      const offlineVersion = offlineWorkOrderMap.get(wo.id);
      wo.downloaded = !!offlineVersion;
      wo.lastSyncedAt = offlineVersion?.lastUpdated ? new Date(offlineVersion.lastUpdated) : null;
    });
  }

  private updateOfflineFallbackBanner() {
    // Offline is determined solely by probe-confirmed reachability: backendReachableStream is
    // only flipped to false after a failed check-update probe. A failed data request that still
    // produced a response (e.g. an HTTP 500 from /work-orders) means the backend is reachable,
    // so it must not force the page offline.
    //
    // We also honour the outcome of the last work-order load: when that load confirmed the
    // backend was unreachable and fell back to downloaded data, the banner must stay visible even
    // if an unrelated request (e.g. a Service Worker cache hit) races backendReachable back to
    // true afterwards.
    this.showOfflineFallbackBanner =
      !this.isOnline || !this.isBackendReachable || this.workOrderService.didDetectBackendUnreachableOnLastLoad();

    // Independently of the offline banner we may still be presenting downloaded content because
    // the online load fell back to IndexedDB (e.g. the project is not authorized). This gates
    // content visibility without implying the backend is unreachable.
    this.isViewingDownloadedContent =
      this.showOfflineFallbackBanner || this.workOrderService.didUseOfflineFallbackOnLastLoad();
  }

  private applyPendingChangeState(events: Array<{ refs: string[] }>): void {
    const nextCounts = new Map<string, number>();

    events.forEach((event) => {
      (event.refs || []).forEach((ref) => {
        nextCounts.set(ref, (nextCounts.get(ref) ?? 0) + 1);
      });
    });

    const trackedWorkOrderIds = new Set<string>([
      ...Array.from(this.pendingChangesByWorkOrder.keys()),
      ...this.workOrders.map((wo) => wo.id),
    ]);

    trackedWorkOrderIds.forEach((workOrderId) => {
      const previousCount = this.pendingChangesByWorkOrder.get(workOrderId) ?? 0;
      const nextCount = nextCounts.get(workOrderId) ?? 0;

      if (previousCount > 0 && nextCount === 0) {
        this.workOrdersWithSavedChanges.add(workOrderId);
      }

      if (nextCount > 0) {
        this.workOrdersWithSavedChanges.delete(workOrderId);
      }

      this.pendingChangesByWorkOrder.set(workOrderId, nextCount);
    });

    this.pendingChangeCount = events.length;
    this.pendingWorkOrderCount = new Set(events.flatMap((e) => e.refs)).size;
    this.applyPerCardPendingChangeLabels();
  }

  private applyPerCardPendingChangeLabels(): void {
    this.workOrders.forEach((workOrder) => {
      workOrder.pendingOfflineChanges = this.pendingChangesByWorkOrder.get(workOrder.id) ?? 0;
      workOrder.showChangesSaved = this.workOrdersWithSavedChanges.has(workOrder.id);
    });
  }

  hasStatusBanner(): boolean {
    return this.showOfflineFallbackBanner || this.onlineSyncBannerState !== 'hidden';
  }

  dismissSavedBanner(): void {
    this.onlineSyncBannerState = 'hidden';
  }

  getStatusBannerClass(): string {
    return this.onlineSyncBannerState === 'saved' ? 'status-saved' : 'status-offline';
  }

  getStatusBannerIcon(): string {
    if (this.onlineSyncBannerState === 'saved') {
      return 'wifi';
    }

    if (this.onlineSyncBannerState === 'syncing') {
      return 'sync';
    }

    return 'wifi_off';
  }
}
